// 📝 Sistema de Logging Centralizado para Frontend
// Envía todos los errores y warnings al backend automáticamente

class Logger {
    constructor() {
        this.API_URL = process.env.REACT_APP_API_URL || '';
        this.usuario = null;
        this.queue = [];
        this.sending = false;
        this.maxRetries = 3;

        // 🚨 NUEVO: Sistema de rate limiting global
        this.globalRateLimit = new Map(); // Para rate limiting por tipo de mensaje
        this.lastLogTime = 0;
        this.logCount = 0;
        this.maxLogsPerMinute = 20; // 👈 LÍMITE: Máximo 20 logs por minuto

        // 🎯 Auto-inicializar captura de errores
        this.initErrorCapture();
    }

    // 🔧 Configurar usuario actual
    setUser(usuario) {
        this.usuario = usuario;
    }

    // 🧹 NUEVO: Función para limpiar/resetear el logger
    cleanup() {
        console.log('🧹 Limpiando sistema de logging...');
        this.queue = [];
        this.sending = false;
        this.debugThrottle = {};
        this.globalRateLimit.clear(); // 👈 NUEVO: Limpiar rate limiting
        this.logCount = 0; // 👈 NUEVO: Reset contador
        this.lastLogTime = 0; // 👈 NUEVO: Reset tiempo
        console.log('✅ Logger limpiado completamente');
    }

    // 📊 NUEVO: Función para obtener estadísticas del logger
    getStats() {
        const now = Date.now();
        const timeSinceLastReset = now - this.lastLogTime;

        return {
            queueLength: this.queue.length,
            sending: this.sending,
            usuario: this.usuario,
            debugThrottleKeys: this.debugThrottle ? Object.keys(this.debugThrottle).length : 0,
            // 🚨 NUEVO: Estadísticas de rate limiting
            rateLimiting: {
                logsThisMinute: this.logCount,
                maxLogsPerMinute: this.maxLogsPerMinute,
                timeSinceLastReset: Math.floor(timeSinceLastReset / 1000),
                rateLimitedMessages: this.globalRateLimit.size,
                rateLimitActive: this.logCount >= this.maxLogsPerMinute
            }
        };
    }

    // 📝 Función principal de logging MEJORADA CON RATE LIMITING
    async log(nivel, mensaje, extras = {}) {
        // 🚨 NUEVO: Rate limiting global
        const now = Date.now();

        // Reset contador cada minuto
        if (now - this.lastLogTime > 60000) {
            this.logCount = 0;
            this.lastLogTime = now;
        }

        // 🛑 NUEVO: Bloquear si se excede el límite
        if (this.logCount >= this.maxLogsPerMinute) {
            console.warn(`🚨 Rate limit excedido: ${this.logCount}/${this.maxLogsPerMinute} logs por minuto`);
            return; // No procesar más logs
        }

        // 🔇 NUEVO: Rate limiting por tipo de mensaje
        const messageKey = `${nivel}_${extras.tipo || 'GENERAL'}_${mensaje.substring(0, 50)}`;
        const lastTime = this.globalRateLimit.get(messageKey) || 0;
        const timeDiff = now - lastTime;

        // 🚫 NUEVO: Evitar mensajes duplicados muy frecuentes
        const minInterval = nivel === 'debug' ? 30000 : nivel === 'info' ? 10000 : 5000;
        if (timeDiff < minInterval) {
            console.log(`🔇 Mensaje throttled: ${messageKey} (${timeDiff}ms < ${minInterval}ms)`);
            return; // Skip este log
        }

        this.globalRateLimit.set(messageKey, now);
        this.logCount++;

        // 🔧 NUEVO: Truncar datos para evitar overflow
        const logData = {
            nivel,
            mensaje: (mensaje || '').substring(0, 300), // 👈 Limitar mensaje
            stack: (extras.stack || '').substring(0, 500), // 👈 Limitar stack
            url: window.location.href.substring(0, 200), // 👈 Limitar URL
            linea: extras.linea || null,
            columna: extras.columna || null,
            userAgent: navigator.userAgent.substring(0, 150), // 👈 Limitar UA
            timestamp: new Date().toISOString(),
            usuario: (this.usuario || 'anónimo').substring(0, 50),
            tipo: (extras.tipo || 'GENERAL').substring(0, 50),
            datos: extras.datos ? JSON.stringify(extras.datos).substring(0, 300) : null // 👈 Limitar datos
        };

        // 🖥️ Siempre logear en consola local también
        const consoleMethod = nivel === 'error' ? 'error' : nivel === 'warn' ? 'warn' : 'log';
        console[consoleMethod](`[${nivel.toUpperCase()}] ${mensaje}`, extras);

        // 📤 Enviar al backend
        await this.sendToBackend(logData);
    }

    // 🔴 Métodos de conveniencia
    error(mensaje, extras = {}) {
        return this.log('error', mensaje, { ...extras, tipo: extras.tipo || 'ERROR' });
    }

    warn(mensaje, extras = {}) {
        return this.log('warn', mensaje, { ...extras, tipo: extras.tipo || 'WARNING' });
    }

    info(mensaje, extras = {}) {
        return this.log('info', mensaje, { ...extras, tipo: extras.tipo || 'INFO' });
    }

    debug(mensaje, extras = {}) {
        // 🚨 NUEVO: Solo debug en desarrollo Y con throttling
        if (process.env.NODE_ENV === 'development') {
            const key = `debug_${extras.tipo || 'GENERAL'}_${mensaje.substring(0, 50)}`;
            const now = Date.now();

            // 🔇 Throttling: Solo permitir el mismo debug cada 30 segundos
            if (!this.debugThrottle) this.debugThrottle = {};
            if (this.debugThrottle[key] && (now - this.debugThrottle[key] < 30000)) {
                return; // Skippear logs debug repetidos
            }
            this.debugThrottle[key] = now;

            return this.log('debug', mensaje, { ...extras, tipo: extras.tipo || 'DEBUG' });
        }
    }

    // 🌐 Logging específico para errores de red
    networkError(url, error, response = null) {
        const mensaje = `Error de red en ${url}: ${error.message}`;
        const extras = {
            tipo: 'NETWORK_ERROR',
            datos: {
                url,
                error: error.message,
                status: response?.status,
                statusText: response?.statusText
            }
        };
        return this.error(mensaje, extras);
    }

    // 📱 Logging específico para errores de GPS
    gpsError(mensaje, error) {
        const extras = {
            tipo: 'GPS_ERROR',
            datos: {
                error: error.message,
                code: error.code,
                permissions: navigator.permissions ? 'supported' : 'not_supported'
            }
        };
        return this.error(`GPS Error: ${mensaje}`, extras);
    }

    // 🔄 Logging específico para errores de fichaje
    fichajeError(usuario, tipo_fichaje, error) {
        const mensaje = `Error en fichaje ${tipo_fichaje} para usuario ${usuario}`;
        const extras = {
            tipo: 'FICHAJE_ERROR',
            datos: {
                usuario,
                tipo_fichaje,
                error: error.message
            }
        };
        return this.error(mensaje, extras);
    }

    // 📤 Enviar logs al backend con cola y reintentos - OPTIMIZADO
    async sendToBackend(logData) {
        this.queue.push(logData);

        // 🚨 NUEVO: Solo iniciar procesamiento si realmente no se está enviando
        if (!this.sending && this.queue.length > 0) {
            this.sending = true;
            this.processQueue();
        }
    }

    async processQueue() {
        if (this.queue.length === 0) {
            this.sending = false;
            return;
        }

        this.sending = true;
        const logData = this.queue.shift();

        for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

                const response = await fetch(`${this.API_URL}/api/log-frontend`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(logData),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (response.ok) {
                    break; // ✅ Éxito, salir del loop de reintentos
                } else if (response.status === 503) {
                    // 🚨 Circuit breaker activado - parar intentos
                    console.warn('⚠️ Logging backend temporalmente deshabilitado');
                    break;
                } else {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.warn('⚠️ Timeout enviando log al backend');
                }

                if (attempt === this.maxRetries) {
                    // ❌ Último intento fallido - logear solo en consola local (compacto)
                    console.error(`❌ Log failed después de ${this.maxRetries} intentos:`, logData.mensaje?.substring(0, 100));
                }
                // 🔄 Esperar antes del siguiente intento (backoff exponencial)
                await new Promise(resolve => setTimeout(resolve, Math.min(attempt * 1000, 5000)));
            }
        }

        // 🔄 Procesar siguiente elemento en la cola SOLO si hay más elementos
        if (this.queue.length > 0) {
            setTimeout(() => this.processQueue(), this.queue.length > 10 ? 1000 : 100);
        } else {
            this.sending = false; // 👈 CRÍTICO: Marcar como no enviando cuando la cola está vacía
        }
    }

    // 🎯 Inicializar captura automática de errores
    initErrorCapture() {
        // 💥 Capturar errores no manejados de JavaScript
        window.addEventListener('error', (event) => {
            this.error('Error JS no manejado', {
                tipo: 'UNHANDLED_ERROR',
                stack: event.error?.stack,
                linea: event.lineno,
                columna: event.colno,
                datos: {
                    filename: event.filename,
                    message: event.message
                }
            });
        });

        // 🔄 Capturar promises rechazadas no manejadas
        window.addEventListener('unhandledrejection', (event) => {
            this.error('Promise rechazada no manejada', {
                tipo: 'UNHANDLED_PROMISE_REJECTION',
                stack: event.reason?.stack,
                datos: {
                    reason: event.reason?.toString() || 'Unknown reason'
                }
            });
        });

        // 📱 Capturar cambios de visibilidad (útil para móviles) - OPTIMIZADO
        let lastVisibilityLog = 0;
        document.addEventListener('visibilitychange', () => {
            const now = Date.now();
            // 🚨 NUEVO: Solo logear cambios de visibilidad cada 5 segundos para evitar spam
            if (now - lastVisibilityLog > 5000) {
                lastVisibilityLog = now;
                if (document.hidden) {
                    this.debug('App en background');
                } else {
                    this.debug('App en foreground');
                }
            }
        });
    }

    // 🔧 Wrapper para fetch que captura errores automáticamente
    async fetchWithLogging(url, options = {}) {
        try {
            const response = await fetch(url, options);

            if (!response.ok) {
                this.networkError(url, new Error(`HTTP ${response.status}: ${response.statusText}`), response);
            }

            return response;
        } catch (error) {
            this.networkError(url, error);
            throw error; // Re-lanzar para que el código que llama pueda manejarlo
        }
    }
}

// 🏭 Crear instancia singleton
const logger = new Logger();

export default logger;
